import type { GetQuestionTypesQuery } from 'src/graphql';
import { API_TYPE_TO_UI_TYPE } from '../../types';
import {
  QUESTION_TYPE_ORDER,
  UI_LABEL_OVERRIDES,
} from './questionCardConstants';

type AnswerTypeOption = {
  label: string;
  value: string;
  allowsOptions: boolean;
  databaseFieldSubTypes?: Array<{
    id: string;
    subTypeKey: string;
    label: string;
    description?: string | null;
  }> | null;
};

function mapApiQuestionType(
  qt: NonNullable<
    GetQuestionTypesQuery['getQuestionTypes']
  >['questionTypes'][number],
): AnswerTypeOption {
  return {
    label: UI_LABEL_OVERRIDES[qt.typeKey] || qt.label,
    value: API_TYPE_TO_UI_TYPE[qt.typeKey] || qt.typeKey,
    allowsOptions: qt.allowsOptions,
    databaseFieldSubTypes: qt.databaseFieldSubTypes,
  };
}

export function buildAnswerTypeOptions(
  questionTypesData: GetQuestionTypesQuery | undefined,
) {
  const apiQuestionTypes = questionTypesData?.getQuestionTypes?.questionTypes;

  if (!apiQuestionTypes || apiQuestionTypes.length === 0) {
    return [];
  }

  // Only use question types returned from API (no fallback to hardcoded)
  const typeMap = new Map(apiQuestionTypes.map((qt) => [qt.typeKey, qt]));

  // Sort by predefined order, but include any unknown types from API at the end
  const orderedTypeKeys = [
    ...QUESTION_TYPE_ORDER.filter((typeKey) => typeMap.has(typeKey)),
    ...apiQuestionTypes
      .map((qt) => qt.typeKey)
      .filter((typeKey) => !QUESTION_TYPE_ORDER.includes(typeKey)),
  ];

  return orderedTypeKeys.map((typeKey) =>
    mapApiQuestionType(typeMap.get(typeKey)!),
  );
}

export function buildDatabaseFieldSubTypeOptions(
  questionTypesData: GetQuestionTypesQuery | undefined,
) {
  const databaseFieldType =
    questionTypesData?.getQuestionTypes?.questionTypes?.find(
      (qt) => qt.typeKey === 'database_field',
    );

  if (!databaseFieldType?.databaseFieldSubTypes?.length) {
    return [{ label: 'No Fields', value: 'no_fields', isDisabled: true }];
  }

  return databaseFieldType.databaseFieldSubTypes.map((subType) => ({
    label: subType.label,
    value: subType.id,
    subTypeKey: subType.subTypeKey,
  }));
}
